import { Component, ElementRef, EventEmitter, HostListener, Input, OnChanges, Output, SimpleChanges, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { finalize } from 'rxjs/operators';
import { Image, IMAGE_TYPE_LABELS } from '../../../../interfaces/Image.interfaces';
import { DamageManagementService } from '../../../../services/damage-management.service';
import { NotificationService } from '../../../../services/notification.service';

/**
 * Sezione "Immagini" del dettaglio pratica danno: le immagini e i documenti
 * (Opr_Images) del collo della pratica, letti dall'endpoint dedicato
 * GET /damaged-parcels/{damageId}/images. Stessa resa della tab Immagini del popup
 * dettaglio collo di /shipment-management: vista compatta a griglia o lista,
 * anteprima a schermo intero, download; i PDF si aprono in una nuova scheda.
 * Con canUpload, carica nuove immagini sul collo (POST /damaged-parcels/{id}/images,
 * tipo DAMAGE): il selettore file è qui, il bottone "Carica immagine" è
 * nell'intestazione della card, che chiama openFilePicker().
 * Il contenitore (card e intestazione) è della pagina: qui solo il corpo.
 */
@Component({
  selector: 'app-damage-images',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './damage-images.component.html',
  styleUrls: ['./damage-images.component.css']
})
export class DamageImagesComponent implements OnChanges {
  @Input() damageId = 0;
  /** Codice del collo della pratica, solo per i messaggi e i nomi dei file */
  @Input() parcelCode = '';
  /** True se l'utente può caricare immagini (permesso UPDATE sulla funzione damages) */
  @Input() canUpload = false;
  /** Numero di immagini caricate, per il contatore nell'intestazione della card */
  @Output() countChange = new EventEmitter<number>();

  @ViewChild('fileInput') fileInput?: ElementRef<HTMLInputElement>;

  images: Image[] = [];
  isLoading = false;
  errorMessage = '';
  isUploading = false;
  viewMode: 'grid' | 'list' = 'grid';
  previewImage: Image | null = null;

  constructor(
    private damageService: DamageManagementService,
    private notificationService: NotificationService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['damageId']) {
      this.previewImage = null;
      this.load();
    }
  }

  /** Esc chiude l'anteprima */
  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.previewImage) this.closePreview();
  }

  /** Carica (o ricarica) le immagini della pratica */
  load(): void {
    if (!this.damageId) {
      this.images = [];
      this.countChange.emit(0);
      return;
    }
    this.isLoading    = true;
    this.errorMessage = '';
    this.damageService.getDamageImages(this.damageId).pipe(
      finalize(() => { this.isLoading = false; })
    ).subscribe({
      next: images => {
        // Dalla più recente
        this.images = [...images].sort((a, b) =>
          new Date(b.imageCreatedAt || 0).getTime() - new Date(a.imageCreatedAt || 0).getTime());
        this.countChange.emit(this.images.length);
      },
      error: (err: Error) => {
        this.images       = [];
        this.errorMessage = err?.message || 'Errore nel caricamento delle immagini. Riprova più tardi.';
        this.countChange.emit(0);
      }
    });
  }

  // ─── Caricamento di una nuova immagine ───

  /** Tipi di file accettati dal selettore (immagini e PDF) e dimensione massima (come il backend) */
  readonly uploadAccept = 'image/*,application/pdf';
  private readonly maxUploadBytes = 10 * 1024 * 1024;

  /** Apre il selettore file (dal bottone "Carica immagine" nell'intestazione della card) */
  openFilePicker(): void {
    if (!this.canUpload || this.isUploading || !this.damageId) return;
    this.fileInput?.nativeElement.click();
  }

  /** File scelto: lo legge in Base64 e lo manda al backend, poi ricarica l'elenco */
  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file  = input.files?.[0];
    input.value = '';
    if (!file || !this.canUpload || this.isUploading || !this.damageId) return;

    const mimeType = (file.type || '').toLowerCase();
    if (!mimeType.startsWith('image/') && mimeType !== 'application/pdf') {
      this.notificationService.warning('Sono ammessi solo immagini e PDF');
      return;
    }
    if (file.size > this.maxUploadBytes) {
      this.notificationService.warning(`Il file supera la dimensione massima di ${this.formatSize(this.maxUploadBytes)}`);
      return;
    }

    this.isUploading = true;
    const reader = new FileReader();
    reader.onerror = () => {
      this.isUploading = false;
      this.notificationService.error('Errore nella lettura del file');
    };
    reader.onload = () => {
      const dataUrl = String(reader.result || '');
      const contentBase64 = dataUrl.substring(dataUrl.indexOf(',') + 1);
      this.damageService.uploadDamageImage(this.damageId, { fileName: file.name, mimeType, contentBase64 }).pipe(
        finalize(() => { this.isUploading = false; })
      ).subscribe({
        next: () => {
          this.notificationService.success(`Immagine "${file.name}" caricata sul collo ${this.parcelCode || ''}`.trim());
          this.load();
        },
        error: (err: Error) => {
          this.notificationService.error(err?.message || 'Errore nel caricamento dell\'immagine. Riprova più tardi.');
        }
      });
    };
    reader.readAsDataURL(file);
  }

  // ─── Resa delle immagini (come il popup dettaglio collo) ───

  typeLabel(image: Image): string {
    const type = (image.imageType || '').toUpperCase();
    return IMAGE_TYPE_LABELS[type] || image.imageType || 'Immagine';
  }

  isPdf(image: Image): boolean {
    return (image.imageMimeType || '').toLowerCase() === 'application/pdf'
      || (image.imageExtension || '').toLowerCase().includes('pdf')
      || (image.imageType || '').toUpperCase() === 'POD_PDF';
  }

  private base64Of(image: Image): string | null {
    const raw = image.imageContentBase64 || image.imageContentByte;
    return raw ? raw.replace(/\s/g, '') : null;
  }

  /** Data URL per il tag img (null per PDF o senza contenuto) */
  src(image: Image): string | null {
    if (this.isPdf(image)) return null;
    const base64 = this.base64Of(image);
    return base64 ? `data:${image.imageMimeType || 'image/jpeg'};base64,${base64}` : null;
  }

  formatSize(bytes: number | undefined): string {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  private base64ToBlob(base64: string, mimeType: string): Blob {
    const chars = atob(base64);
    const bytes = new Uint8Array(chars.length);
    for (let i = 0; i < chars.length; i++) bytes[i] = chars.charCodeAt(i);
    return new Blob([bytes], { type: mimeType });
  }

  /** Anteprima a schermo intero; i PDF si aprono in una nuova scheda */
  openPreview(image: Image): void {
    if (this.isPdf(image)) {
      const base64 = this.base64Of(image);
      if (!base64) {
        this.notificationService.error('Contenuto del documento non disponibile');
        return;
      }
      try {
        window.open(URL.createObjectURL(this.base64ToBlob(base64, 'application/pdf')), '_blank');
      } catch {
        this.notificationService.error('Errore nell\'apertura del documento');
      }
      return;
    }
    if (!this.src(image)) {
      this.notificationService.warning('Contenuto dell\'immagine non disponibile');
      return;
    }
    this.previewImage = image;
  }

  closePreview(): void {
    this.previewImage = null;
  }

  download(image: Image, event?: Event): void {
    event?.stopPropagation();
    const base64 = this.base64Of(image);
    if (!base64) {
      this.notificationService.error('Contenuto dell\'immagine non disponibile');
      return;
    }
    try {
      const mimeType  = image.imageMimeType || 'application/octet-stream';
      const code      = image.imageParcelCode || this.parcelCode || 'parcel';
      const type      = (image.imageType || 'IMG').toUpperCase();
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').substring(0, 19);
      const extension = (image.imageExtension || '').replace('.', '')
        || (mimeType.includes('/') ? mimeType.split('/').pop() : '') || '';
      const url  = URL.createObjectURL(this.base64ToBlob(base64, mimeType));
      const link = document.createElement('a');
      link.href     = url;
      link.download = `${code}_${type}_${timestamp}${extension ? '.' + extension : ''}`;
      link.click();
      URL.revokeObjectURL(url);
    } catch {
      this.notificationService.error('Errore nel download dell\'immagine');
    }
  }

  trackByImage(index: number, image: Image): number {
    return image.imageId ?? index;
  }
}
